import type { QueryRequestData } from '@ricoh/ria.client.core/plugins/swr/request';

import { createEndpointConfig } from '@ricoh/ria.client.core/plugins/swr/common';
import { createQueryRequest } from '@ricoh/ria.client.core/plugins/swr/request';
import { isNewKey } from '@ricoh/ria.client.core/utils/data/key';
import { transformFilters } from '@ricoh/ria.client.core/utils/data/query';
import { listTransform } from '@ricoh/ria.client.core/utils/mapper/list';

import type { DraftBatch } from '~/common/model/api/batch';

import { transformProcessBatch } from '~/services/server/api/batch/mapper';
import { transformScanningOptions } from '~/services/server/api/scanning-profile/mapper';
import { transformClassificationOptions, transformSeparationOptions } from '~/services/server/api/separation-profile/mapper';

import type {
  AddSourceOfflineRequestData,
  AddSourceOnlineRequestData,
  CreateBatchRequestData,
  GetBatchFieldDataSourceRequestData,
  GetDataSourceRequestData,
  GetDocumentFieldDataSourceRequestData,
  GetDocumentFieldsRequestData,
  GetDocumentLayoutRequestData,
  GetOCRPageRequestData,
  MergeBatchRequestData,
  RemoveBatchesRequestData,
  SaveBatchRequestData,
  SaveProfilesRequestData,
  SubmitBatchRequestData,
  ValidateFieldsRequestData,
} from './_request.type';

import createRequestConfig from '../_config';

const FIELD = {
  CREATED_AT: 'createdAt',
  LAST_UPDATED_AT: 'lastUpdatedAt',
} satisfies Record<string, keyof DraftBatch>;

/** Batch request config. */
const requestConfig = createRequestConfig('batch', (keyBase) => ({
  getAssignedBatchTypeValueSet: createEndpointConfig(`[get] ${keyBase}/batch/batch-types`, () => ({
    method: 'get',
    url: 'batch/batch-types',
  })),
  getDefaultBatchPreferences: createEndpointConfig(`[get] ${keyBase}/batch/default`, () => ({
    method: 'get',
    url: 'batch/default',
  })),

  getBatchFields: createEndpointConfig(`[get] ${keyBase}/batch/:batchId/fields`, (id: string) => ({
    method: 'get',
    url: `batch/${id}/fields`,
  })),
  getDocumentFields: createEndpointConfig(
    `[get] ${keyBase}/batch/:batchId/documents/:documentId/fields`,
    ({ batchId, documentId }: GetDocumentFieldsRequestData) => ({
      method: 'get',
      url: `batch/${batchId}/documents/${documentId}/fields`,
    }),
  ),

  accessBatch: createEndpointConfig(`[get] ${keyBase}/batch/:batchId/access`, (id: string) => ({
    method: 'get',
    url: `batch/${id}/access`,
  })),
  getBatchView: createEndpointConfig(`[get] ${keyBase}/batch/:batchId/view`, (id: string) => ({
    method: 'get',
    url: `batch/${id}/view`,
  })),

  createBatch: createEndpointConfig(`[post] ${keyBase}/batch`, ({ batchTypeName, priority, startAt }: CreateBatchRequestData) => ({
    method: 'post',
    url: 'batch',
    data: {
      type: {
        name: batchTypeName,
      },
      priority,
      startAt,
    },
  })),
  saveBatch: createEndpointConfig(`[post] ${keyBase}/batch/:batchId/save`, ({ id, status, ...data }: SaveBatchRequestData) => ({
    method: 'post',
    url: `batch/${id}/save`,
    data: {
      status,
      ...transformProcessBatch(data),
    },
  })),
  mergeBatch: createEndpointConfig(`[post] ${keyBase}/batch/:batchId/merge/:targetBatchId`, ({ id, targetBatchId, ...data }: MergeBatchRequestData) => ({
    method: 'post',
    url: `batch/${id}/merge/${targetBatchId}`,
    data: transformProcessBatch(data),
  })),

  getSourceOnline: createEndpointConfig(`[get] ${keyBase}/batch/:batchId/source/:fileKey`, (id: string, fileKey: string) => ({
    method: 'get',
    url: `batch/${id}/source/${fileKey}`,
  })),
  addSourceOnline: createEndpointConfig(`[post] ${keyBase}/batch/:batchId/source-online`, ({ id, sourceType, files }: AddSourceOnlineRequestData) => {
    const formData = new FormData();
    formData.append('sourceType', sourceType);
    files.forEach((file) => {
      formData.append('files', file, file.name);
    });

    return {
      method: 'post',
      url: `batch/${id}/source-online`,
      data: formData,
    };
  }),
  addSourceOnlineManual: createEndpointConfig(`[patch] ${keyBase}/batch/:batchId/source-online`, ({ id, sourceType, files }: AddSourceOnlineRequestData) => {
    const formData = new FormData();
    formData.append('sourceType', sourceType);
    files.forEach((file) => {
      formData.append('files', file, file.name);
    });

    return {
      method: 'patch',
      url: `batch/${id}/source-online`,
      data: formData,
    };
  }),
  cleanSourceOnline: createEndpointConfig(`[delete] ${keyBase}/batch/:batchId/source`, (id: string) => ({
    method: 'delete',
    url: `batch/${id}/source`,
  })),

  getSourceOffline: createEndpointConfig(`[get] ${keyBase}/batch/:batchId/source-offline/:fileKey`, (id: string, fileKey: string) => ({
    method: 'get',
    url: `batch/${id}/source-offline/${fileKey}`,
  })),
  addSourceOffline: createEndpointConfig(`[post] ${keyBase}/batch/:batchId/source-offline`, ({ id, sourceType, files }: AddSourceOfflineRequestData) => {
    const formData = new FormData();
    formData.append('sourceType', sourceType);
    files.forEach((file) => {
      formData.append('files', file, file.name);
    });

    return {
      method: 'post',
      url: `batch/${id}/source-offline`,
      data: formData,
    };
  }),

  deleteBatch: createEndpointConfig(`[delete] ${keyBase}/batch/:batchId`, (id: string) => ({
    method: 'delete',
    url: `batch/${id}`,
  })),

  removeBatches: createEndpointConfig(`[post] ${keyBase}/batch/remove`, (data: RemoveBatchesRequestData) => ({
    method: 'delete',
    url: `batch/remove`,
    data,
  })),

  getDraftList: createEndpointConfig(`[post] ${keyBase}/batch/drafts-list`, (query: QueryRequestData) => {
    const { headers, params } = createQueryRequest(query, {
      transformFilter: (filterDescriptor) => {
        if (filterDescriptor.field === FIELD.CREATED_AT || filterDescriptor.field === FIELD.LAST_UPDATED_AT) {
          return transformFilters.date(filterDescriptor, {
            withoutTimeZone: true,
          });
        }

        return [filterDescriptor];
      },
    });

    return {
      method: 'post',
      url: 'batch/drafts-list',
      headers,
      data: params,
    };
  }),

  submitBatch: createEndpointConfig(`[post] ${keyBase}/batch/:batchId/submit`, ({ id, status }: SubmitBatchRequestData) => ({
    method: 'post',
    url: `batch/${id}/submit`,
    data: {
      status,
    },
  })),

  getDocumentLayout: createEndpointConfig(
    `[get] ${keyBase}/batch/:batchId/documents/:documentId/layout`,
    ({ batchId, documentId }: GetDocumentLayoutRequestData) => ({
      method: 'get',
      url: `batch/${batchId}/documents/${documentId}/layout`,
    }),
  ),
  getOCRPage: createEndpointConfig(`[get] ${keyBase}/batch/:batchId/page/:pageId/ocr`, ({ batchId, pageId }: GetOCRPageRequestData) => ({
    method: 'get',
    url: `batch/${batchId}/page/${pageId}/ocr`,
  })),

  getProfiles: createEndpointConfig(`[get] ${keyBase}/batch/type/:batchId/profiles`, (id: string) => ({
    method: 'get',
    url: `batch/type/${id}/profiles`,
  })),
  saveProfiles: createEndpointConfig(
    `[post] ${keyBase}/batch/type/:batchId/profiles`,
    ({ batchTypeId, profiles: { scanning, separation } }: SaveProfilesRequestData) => ({
      method: 'post',
      url: `batch/type/${batchTypeId}/profiles`,
      data: {
        scanner: transformScanningOptions(scanning.options),
        separation: transformSeparationOptions(separation.separationOptions),
        classification: transformClassificationOptions(separation.classificationOptions),
      },
    }),
  ),

  getBatchDataSource: createEndpointConfig(
    `[get] ${keyBase}/batch/:batchId/data-source`,
    ({ batchId, documentId, batchFields, documentFields }: GetDataSourceRequestData) => ({
      method: 'post',
      url: `batch/${batchId}/data-source`,
      data: {
        fields: listTransform.toRecord(batchFields, {
          transform: (field) => field.value ?? {},
        }),
        documents:
          documentId && !isNewKey(documentId)
            ? {
                [documentId]: {
                  fields: listTransform.toRecord(documentFields, {
                    transform: (field) => field.value ?? {},
                  }),
                },
              }
            : {},
      },
    }),
  ),
  getDocumentDataSource: createEndpointConfig(
    `[post] ${keyBase}/batch/:batchId/documents/:documentId/data-source`,
    ({ batchId, documentId, batchFields, documentFields }: GetDataSourceRequestData) => ({
      method: 'post',
      url: `batch/${batchId}/documents/${documentId}/data-source`,
      data: {
        fields: listTransform.toRecord(batchFields, {
          transform: (field) => field.value ?? {},
        }),
        documents:
          documentId && !isNewKey(documentId)
            ? {
                [documentId]: {
                  fields: listTransform.toRecord(documentFields, {
                    transform: (field) => field.value ?? {},
                  }),
                },
              }
            : {},
      },
    }),
  ),

  getBatchFieldDataSource: createEndpointConfig(
    `[get] ${keyBase}/batch/:batchId/fields/:fieldId/data-source`,
    ({ batchId, documentId, batchFields, documentFields, batchFieldId }: GetBatchFieldDataSourceRequestData) => ({
      method: 'post',
      url: `batch/${batchId}/fields/${batchFieldId}/data-source`,
      data: {
        fields: listTransform.toRecord(batchFields, {
          transform: (field) => field.value ?? {},
        }),
        documents:
          documentId && !isNewKey(documentId)
            ? {
                [documentId]: {
                  fields: listTransform.toRecord(documentFields, {
                    transform: (field) => field.value ?? {},
                  }),
                },
              }
            : {},
      },
    }),
  ),
  getDocumentFieldDataSource: createEndpointConfig(
    `[post] ${keyBase}/batch/:batchId/documents/:documentId/:fieldId/data-source`,
    ({ batchId, documentId, batchFields, documentFields, documentFieldId }: GetDocumentFieldDataSourceRequestData) => ({
      method: 'post',
      url: `batch/${batchId}/documents/${documentId}/fields/${documentFieldId}/data-source`,
      data: {
        fields: listTransform.toRecord(batchFields, {
          transform: (field) => field.value ?? {},
        }),
        documents:
          documentId && !isNewKey(documentId)
            ? {
                [documentId]: {
                  fields: listTransform.toRecord(documentFields, {
                    transform: (field) => field.value ?? {},
                  }),
                },
              }
            : {},
      },
    }),
  ),

  validateFields: createEndpointConfig(
    `[post] ${keyBase}/batch/:batchId/field-validations`,
    ({ batchId, documentId, batchFields, documentFields, validationFields }: ValidateFieldsRequestData) => ({
      method: 'post',
      url: `batch/${batchId}/field-validations`,
      data: {
        fields: listTransform.toRecord(batchFields, {
          transform: (field) => field.value ?? {},
        }),
        documents:
          documentId && !isNewKey(documentId)
            ? {
                [documentId]: {
                  fields: listTransform.toRecord(documentFields, {
                    transform: (field) => field.value ?? {},
                  }),
                },
              }
            : {},
        validationFields: {
          batchFields: validationFields.batchFields,
          documentFields:
            documentId && !isNewKey(documentId)
              ? {
                  [documentId]: validationFields.documentFields,
                }
              : {},
        },
      },
    }),
  ),

  extendLockBatch: createEndpointConfig(`[get] ${keyBase}/batch/:batchId/lock`, (id: string) => ({
    method: 'patch',
    url: `batch/${id}/lock`,
  })),
  unlockBatch: createEndpointConfig(`[get] ${keyBase}/batch/:batchId/lock`, (id: string) => ({
    method: 'delete',
    url: `batch/${id}/lock`,
  })),
}));

export default requestConfig;
